import { z } from 'zod';
import { IsoDateTime, PageMeta, PageRequest, MutationReason } from './_common.js';
// ============================================================
// Common user shapes
// ============================================================
export const UserTier = z.enum(['bronze', 'silver', 'gold', 'platinum', 'diamond']);
export const UserBanStatus = z.enum(['none', 'temporary', 'permanent']);
/**
 * Lightweight summary, used in list / search responses.
 */
export const AdminUserSummary = z.object({
    user_id: z.string(),
    username: z.string(),
    display_name: z.string(),
    avatar_url: z.string().nullable(),
    active_frame_id: z.string().nullable(),
    email: z.string().email().nullable(),
    current_tier: UserTier,
    balance: z.number().int(),
    last_seen_at: IsoDateTime.nullable(),
    created_at: IsoDateTime,
    ban_status: UserBanStatus,
    viral_blocked: z.boolean(),
    is_synthetic: z.boolean(),
    country_code: z.string().nullable(), // ISO 3166-1 alpha-2; e.g. 'US' / 'CA'
    tags: z.array(z.string()),
});
/**
 * Full detail, used on profile view. Embeds counts but not full lists
 * (full lists go through dedicated endpoints — see ledger / friends / iap modules).
 */
export const AdminUserDetail = AdminUserSummary.extend({
    auth_provider: z.enum(['guest', 'apple', 'google']),
    auth_subject: z.string().nullable(), // OAuth subject; never displayed in UI but useful for debug
    invited_by: z.string().nullable(),
    friend_invite_code: z.string(),
    first_session_at: IsoDateTime.nullable(),
    first_purchase_at: IsoDateTime.nullable(),
    tier_points: z.number().int(),
    tier_points_reset_at: IsoDateTime.nullable(),
    lifetime_tier_points: z.number().int(),
    lifetime_iap_cents: z.number().int(),
    lifetime_fever_top_contributions: z.number().int(),
    // Summary counts (reads cheap aggregates)
    counts: z.object({
        friends_accepted: z.number().int(),
        friends_pending_outgoing: z.number().int(),
        friends_pending_incoming: z.number().int(),
        invites_generated: z.number().int(),
        invites_redeemed: z.number().int(),
        gifts_sent_total: z.number().int(),
        gifts_received_total: z.number().int(),
        iap_receipts_total: z.number().int(),
        spins_lifetime: z.number().int(),
        rooms_joined_lifetime: z.number().int(),
    }),
    // Most recent ban event (null if never banned)
    last_ban: z
        .object({
        banned_at: IsoDateTime,
        banned_by: z.string(),
        reason: z.string(),
        duration_hours: z.number().int().nullable(), // null = permanent
        expires_at: IsoDateTime.nullable(),
    })
        .nullable(),
});
// ============================================================
// Search / list
// ============================================================
export const AdminUserSearchRequest = PageRequest.extend({
    q: z.string().min(1).max(100).optional(), // matches user_id / username / email / display_name
    tier: UserTier.optional(),
    ban_status: UserBanStatus.optional(),
    has_purchase: z.boolean().optional(),
    is_synthetic: z.boolean().optional(),
    country_code: z.string().length(2).optional(),
    last_seen_after: IsoDateTime.optional(),
    last_seen_before: IsoDateTime.optional(),
    created_after: IsoDateTime.optional(),
    created_before: IsoDateTime.optional(),
    tag: z.string().optional(), // single tag filter; AND for multiple is v1.5
    sort: z.enum(['last_seen_desc', 'created_desc', 'balance_desc', 'lifetime_iap_desc']).default('last_seen_desc'),
});
export const AdminUserSearchResponse = z.object({
    items: z.array(AdminUserSummary),
    page: PageMeta,
});
// ============================================================
// Activity timeline (events table, paginated)
// ============================================================
export const AdminUserActivityKind = z.enum([
    'login',
    'spin_settled',
    'room_joined',
    'room_left',
    'reaction_sent',
    'daily_claimed',
    'fever_settled',
    'iap_purchase_completed',
    'gift_sent',
    'gift_received',
    'invite_redeemed',
    'friend_added',
]);
export const AdminUserActivityEntry = z.object({
    id: z.string(),
    kind: AdminUserActivityKind,
    payload: z.record(z.unknown()),
    ts: IsoDateTime,
});
export const AdminUserActivityRequest = PageRequest.extend({
    kind: AdminUserActivityKind.optional(),
    ts_after: IsoDateTime.optional(),
    ts_before: IsoDateTime.optional(),
});
export const AdminUserActivityResponse = z.object({
    items: z.array(AdminUserActivityEntry),
    page: PageMeta,
});
// ============================================================
// Compensate (coin grant / deduct)
// ============================================================
export const AdminCompensateRequest = z.object({
    amount: z.number().int(), // signed; positive = grant, negative = deduct
    reason: MutationReason,
    // category lets reporting roll up: bug | goodwill | event_reward | customer_service | manual
    category: z.enum(['bug', 'goodwill', 'event_reward', 'customer_service', 'manual']).default('manual'),
});
export const AdminCompensateResponse = z.object({
    ledger_entry_id: z.number().int(),
    new_balance: z.number().int(),
    // populated only if amount > 1_000_000 — second admin must approve before ledger writes
    pending_second_approval: z.boolean(),
});
// ============================================================
// Ban / unban
// ============================================================
export const AdminBanRequest = z.object({
    reason: MutationReason,
    duration_hours: z.number().int().min(1).max(8760).nullable().default(null), // null = permanent; 8760 = 1y
});
export const AdminBanResponse = z.object({
    ban_id: z.number().int(),
    expires_at: IsoDateTime.nullable(),
});
export const AdminUnbanRequest = z.object({
    reason: MutationReason,
});
export const AdminUnbanResponse = z.object({
    ok: z.literal(true),
});
// ============================================================
// Viral block / unblock — softer than full ban
// ============================================================
export const AdminViralBlockRequest = z.object({
    reason: MutationReason,
});
export const AdminViralBlockResponse = z.object({
    ok: z.literal(true),
});
export const AdminViralUnblockRequest = AdminViralBlockRequest;
export const AdminViralUnblockResponse = AdminViralBlockResponse;
// ============================================================
// PII scrub (irreversible, 24h cool-off, admin role only)
// ============================================================
export const AdminScrubPiiRequest = z.object({
    reason: MutationReason,
    confirm_irreversible: z.literal(true),
});
export const AdminScrubPiiResponse = z.object({
    scheduled_at: IsoDateTime, // exec time = NOW + 24h
    job_id: z.string(),
});
// ============================================================
// Force log out (invalidate JWT)
// ============================================================
export const AdminForceLogoutRequest = z.object({
    reason: MutationReason,
});
export const AdminForceLogoutResponse = z.object({
    ok: z.literal(true),
});
// ============================================================
// Tags
// ============================================================
export const AdminAddTagRequest = z.object({
    tag: z.string().min(2).max(40).regex(/^[a-z0-9_]+$/),
    reason: MutationReason,
});
export const AdminAddTagResponse = z.object({
    ok: z.literal(true),
    tags: z.array(z.string()),
});
export const AdminRemoveTagRequest = z.object({
    tag: z.string(),
    reason: MutationReason,
});
export const AdminRemoveTagResponse = AdminAddTagResponse;
//# sourceMappingURL=users.js.map